import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Calendar } from 'primereact/calendar';
import * as XLSX from 'xlsx';

import PageHeader from '../components/ui/PageHeader';
import DialogHeader from '../components/ui/DialogHeader';
import ActionToolbar from '../components/ui/ActionToolbar';
import { ExpedienteEntity } from '../entity/ExpedienteEntity';
import './Usuario.css';

export const ReportePrescripcion = () => {

    const emptyExpediente = ExpedienteEntity

    const { axiosInstance } = useAuth();
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [expedienteDialog, setExpedienteDialog] = useState(false);
    const [expediente, setExpediente] = useState(emptyExpediente);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
    });

    // --- NUEVOS ESTADOS PARA REPORTE ---
    const [esModoReporte, setEsModoReporte] = useState(false);
    const [diasVencimiento, setDiasVencimiento] = useState(30);
    // ----------------------------------

    const toast = useRef(null);

    const loadLazyData = useCallback(async () => {
        setLoading(true);
        try {
            // Si estamos en modo reporte, llamamos al nuevo endpoint
            if (esModoReporte) {
                const response = await axiosInstance.get(`/pad-expediente/reporte-vencimiento`, {
                    params: {
                        dias: diasVencimiento,
                        page: lazyParams.page,
                        size: lazyParams.rows
                    }
                });
                setData(response.data.content);
                setTotalRecords(response.data.totalElements);
            } else {
                // Modo normal de búsqueda/listado
                const endpoint = globalFilter ? `/pad-expediente/buscar` : `/pad-expediente`;
                const params = {
                    page: lazyParams.page,
                    size: lazyParams.rows,
                    filtro: globalFilter
                };
                const response = await axiosInstance.get(endpoint, { params });
                setData(response.data.content);
                setTotalRecords(response.data.totalElements);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al cargar expedientes', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [axiosInstance, lazyParams, globalFilter, esModoReporte, diasVencimiento]);

    useEffect(() => {
        loadLazyData();
    }, [loadLazyData]);

    const onPage = (event) => {
        setLazyParams(event);
    };

    // Lógica para resaltar filas próximas a vencer
    const rowClassName = (rowData) => {
        if (!rowData.fecPrescripcion) return '';

        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        // Ajuste de fecha para evitar desfase de zona horaria
        const fechaP = new Date(rowData.fecPrescripcion + 'T00:00:00');
        const diffTime = fechaP - hoy;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return {
            'bg-red-100 text-red-900 font-bold': diffDays <= 7, // Menos de una semana
            'bg-orange-100': diffDays > 7 && diffDays <= 15    // Alerta preventiva
        };
    };

    const personaBody = (rowData) => (
        <span>{`${rowData.persona.nombre} ${rowData.persona.apellido}`} <br />
            <small className="text-muted">{rowData.persona.docIdentidad}</small></span>
    );

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 p-2">
            <div className="flex align-items-center gap-2">
                <Button
                    label={esModoReporte ? "Ver Listado Total" : "Reporte Próximos a Vencer"}
                    icon={esModoReporte ? "pi pi-list" : "pi pi-clock"}
                    className={esModoReporte ? "p-button" : "p-button"}
                    style={{ backgroundColor: 'white', borderColor: '#cccccc' }}
                    onClick={() => {
                        setEsModoReporte(!esModoReporte);
                        setLazyParams({ ...lazyParams, page: 0, first: 0 });
                    }}
                />
                {esModoReporte && (
                    <div className="flex align-items-center gap-2 ml-2">
                        <span className="text-sm font-medium">Días:</span>
                        <InputText
                            type="number"
                            value={diasVencimiento}
                            onChange={(e) => setDiasVencimiento(e.target.value)}
                            style={{ width: '80px' }}
                        />
                    </div>
                )}
            </div>
            <span className="p-input-icon-left">
                <InputText
                    type="search"
                    onInput={(e) => setGlobalFilter(e.target.value)}
                    placeholder="Buscar..."
                />
            </span>
        </div>
    );

    // Plantilla para la columna de fecha con validación de "-"
    const fechaBodyTemplate = (rowData) => {
        return rowData.fecPrescripcion
            ? new Date(rowData.fecPrescripcion + 'T00:00:00').toLocaleDateString()
            : '-';
    };

    // Plantilla para días restantes
    const diasRestantesBodyTemplate = (rowData) => {
        if (!rowData.fecPrescripcion) return '-';
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const fechaP = new Date(rowData.fecPrescripcion + 'T00:00:00');
        const diffDays = Math.ceil((fechaP - hoy) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return <span className="text-red-500">Vencido</span>;
        return <span>{diffDays} días</span>;
    };

    return (
        <div className="usuario-page">
            <div className="container">
                <Toast ref={toast} />

                <PageHeader
                    title="Control de Expedientes PAD"
                    description={esModoReporte ? "Listado de expedientes próximos a la prescripción" : "Administración de expedientes administrativos disciplinarios"}
                    icon="pi pi-folder-open"
                />

                <div className="grid mt-3">
                    <div className="col-12">
                        <div className="card shadow-2 p-3 border-round-xl surface-card">

                            <DataTable
                                value={data}
                                lazy
                                paginator
                                first={lazyParams.first}
                                rows={lazyParams.rows}
                                totalRecords={totalRecords}
                                onPage={onPage}
                                loading={loading}
                                header={header}
                                rowClassName={rowClassName}
                                responsiveLayout="scroll"
                                className="p-datatable-sm"
                                emptyMessage="No se encontraron expedientes."
                            >
                                <Column field="expediente" header="N° Expediente" sortable />
                                <Column header="Persona" body={personaBody} />
                                <Column field="sancion" header="Sanción" />
                                <Column field="estado" header="Estado" />
                                <Column field="fecRegistro" header="Fecha Registro" body={(r) => new Date(r.fecRegistro).toLocaleDateString()} />
                                <Column field="fecPrescripcion" header="Fecha de Prescripción" body={(r) => r.fecPrescripcion ? new Date(r.fecPrescripcion).toLocaleDateString() : '-'} />
                                <Column header="Vence en" body={diasRestantesBodyTemplate} className="font-bold" />
                                <Column body={(rowData) => (
                                    <div className="flex gap-2">
                                        <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" onClick={() => { setExpediente(rowData); setExpedienteDialog(true); }} />
                                    </div>
                                )} />
                            </DataTable>
                        </div>
                    </div>
                </div>

                {/* Diálogo de edición/creación (Simplificado para el ejemplo) */}
                <Dialog
                    visible={expedienteDialog}
                    style={{ width: '450px', maxWidth: '95vw' }}
                    header="Detalle del Expediente"
                    header={
                        <DialogHeader
                            title={<span style={{ color: 'white' }}>{'Editar Expediente'}</span>}
                            subtitle={'Modificar datos del expediente' + (expediente.expediente ? ` - ${expediente.expediente}` : '')}
                            icon="pi pi-pencil"
                        />
                    }
                    modal
                    className="p-fluid"
                    onHide={() => setExpedienteDialog(false)}
                >
                    <div className="field">
                        <label htmlFor="fecPrescripcion">Fecha Prescripción</label>
                        <Calendar id="fecPrescripcion" value={expediente.fecPrescripcion ? new Date(expediente.fecPrescripcion + 'T00:00:00') : null} onChange={(e) => setExpediente({ ...expediente, fecPrescripcion: e.value })} dateFormat="dd/mm/yy" showIcon />
                    </div>
                </Dialog>

            </div>
        </div>
    );
};

export default ReportePrescripcion;